/*
 * Watermarks — DHIS2 sync cursors per scope. Operators reset a watermark
 * when a sync skipped data (e.g. the worker crashed mid-window) so the
 * next run replays from the prior position. Destructive: confirmation
 * before delete.
 */

import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../lib/api.js";
import { SkeletonTable } from "../components/Skeleton.js";

type Watermark = Awaited<ReturnType<typeof api.listWatermarks>>["watermarks"][number];

function parseScope(scope: string): { family: string; workspace?: string; environment?: string; legacy: boolean } {
  if (scope === "tracker-events") return { family: "tracker-events", legacy: true };
  if (scope.startsWith("tracker-events:")) {
    const [, workspace, environment] = scope.split(":");
    return { family: "tracker-events", workspace, environment, legacy: false };
  }
  if (scope.startsWith("facts:")) {
    const [, workspace, environment] = scope.split(":");
    return { family: "submitted-aggregate", workspace, environment, legacy: false };
  }
  return { family: scope, legacy: false };
}

function relTime(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function Watermarks() {
  const [watermarks, setWatermarks] = useState<Watermark[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const refresh = () => {
    api.listWatermarks()
      .then((r) => setWatermarks(r.watermarks))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  };
  useEffect(refresh, []);

  async function reset(w: Watermark) {
    const ok = window.confirm(
      `Reset watermark for ${w.scope} / ${w.key}?\n\nNext sync will replay from the beginning of the available window — this may take a while and re-emit events that were already processed.`
    );
    if (!ok) return;
    const k = `${w.scope}/${w.key}`;
    setBusyKey(k);
    try {
      await api.resetWatermark(w.scope, w.key);
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Watermarks</h1>
          <p className="muted">
            Sync cursors per scope. Reset a row to force the next sync to replay from the available window's start.
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={refresh}>Refresh</button>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      {watermarks?.some((w) => parseScope(w.scope).legacy) && (
        <div className="ts-alert ts-alert-error">
          Legacy global tracker watermarks are still present. Reset &amp; resync the affected DHIS2 connection so scoped tracker cursors replace the old global row.
        </div>
      )}

      {watermarks === null && <SkeletonTable rows={4} columns={6} />}
      {watermarks !== null && watermarks.length === 0 && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No watermarks set</h2>
          <p>Watermarks appear after the first sync completes for each scope. Trigger an aggregate metadata or tracker events sync to populate this view.</p>
        </div>
      )}

      {watermarks && watermarks.length > 0 && (
        <table className="transfer-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Scope</th>
              <th>Key</th>
              <th>Last updated (cursor)</th>
              <th>Updated at</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {watermarks.map((w) => {
              const k = `${w.scope}/${w.key}`;
              const parsed = parseScope(w.scope);
              const source = parsed.environment
                ? `${parsed.family} · ${parsed.environment}`
                : parsed.family;
              return (
                <tr key={k}>
                  <td>
                    <span className={`pill ${parsed.legacy ? "pill-err" : "pill-muted"}`}>{source}</span>
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {parsed.legacy
                      ? "legacy global"
                      : parsed.workspace
                        ? `${parsed.workspace}${parsed.environment ? ` / ${parsed.environment}` : ""}`
                        : w.scope}
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>{w.key}</td>
                  <td title={w.lastUpdated} className="mono" style={{ fontSize: 11 }}>{w.lastUpdated}</td>
                  <td className="muted">{w.updatedAt ? relTime(w.updatedAt) : "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={busyKey === k}
                      onClick={() => void reset(w)}
                    >
                      {busyKey === k ? "Resetting…" : "Reset"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
